import { useEffect, useRef, type ReactNode } from 'react'
import './scroll-rail.css'

type Mode = 'both' | 'fade' | 'arrows' | 'shadow'

type Props = {
  mode?: Mode
  /** Adds `scroll-rail-edge-tap` modifier — entire left/right edge becomes a tap zone (mobile decks). */
  edgeTap?: boolean
  /** Adds `scroll-rail-snap` to the track — tap-to-page snap behavior. */
  snap?: boolean
  className?: string
  children: ReactNode
}

/** React wrapper around the handoff's vanilla `scroll-rail` primitive.
 *  Track must contain direct children to scroll horizontally.
 *
 *  Initialization: the vanilla module's IIFE runs `initAll()` on import and
 *  installs a MutationObserver that catches any `[data-scroll-rail]` element
 *  added to the DOM afterward — including this one. So we don't need to call
 *  `window.ScrollRail.init` ourselves; we just need to ensure the module has
 *  been loaded at least once. (A synchronous init call on first mount would
 *  no-op anyway — the module promise hasn't resolved on the same tick.) */
export function ScrollRail({ mode = 'both', edgeTap, snap, className = '', children }: Props) {
  const railRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Side-effect import — module's IIFE + MutationObserver handle wiring.
    void ensureScrollRailModule()
  }, [])

  const classes = [
    'scroll-rail',
    edgeTap ? 'scroll-rail-edge-tap' : '',
    className,
  ].filter(Boolean).join(' ')

  return (
    <div ref={railRef} className={classes} data-scroll-rail={mode}>
      <div className={`scroll-rail-track${snap ? ' scroll-rail-snap' : ''}`}>
        {children}
      </div>
    </div>
  )
}

/** Lazily evaluates the vanilla scroll-rail module (IIFE side-effect import). */
let loaded = false
async function ensureScrollRailModule() {
  if (loaded) return
  loaded = true
  await import('./scroll-rail-module')
}
